import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { countries } from "countries-list";
import { useAppContext } from "../../Context/AppContext";
import { ImageUrls } from "../../types/Types";
import { YouthMembershipFormData } from "../../types/UserData";
import AgeVerificationForm from "./YouthMembershipForms/AgeVerificationForm";
import StepsProgressBar from "./StepsProgressBar";

const PersonalProfileForm = lazy(
  () => import("./YouthMembershipForms/PersonalProfileForm.tsx")
);
const MentorshipGoalsForm = lazy(
  () => import("./YouthMembershipForms/MentorShipGoalsForm")
);

const STEP_LABELS: string[] = [
  "Age Verification",
  "Personal Information",
  "Mentorship Goals",
];

const LAZY_FALLBACK_MIN_HEIGHT = 200;
const LAZY_FALLBACK_STYLE = { minHeight: LAZY_FALLBACK_MIN_HEIGHT };
const LAZY_FALLBACK = (
  <div
    className="rounded-4 p-3 px-4"
    style={LAZY_FALLBACK_STYLE}
    aria-busy
  />
);

const INFO_ICON_SIZE = 15;

export const renderTooltip = (msg: string) => (
  <Tooltip id="tooltip">{msg}</Tooltip>
);

export const languageOptions = [
  { value: "Arabic", label: "Arabic" },
  { value: "Bahasa Indonesia", label: "Bahasa Indonesia" },
  { value: "English", label: "English" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "Malay", label: "Malay" },
  { value: "Turkish", label: "Turkish" },
];

export const countryOptions = Object.entries(countries)
  .map(([code, country]) => ({
    value: country.name,
    label: country.name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const SectionHeaderComponent = ({
  title,
  tooltip,
}: {
  title: string;
  tooltip: string;
}) => {
  const overlay = useMemo(
    () => <Tooltip id="tooltip">{tooltip}</Tooltip>,
    [tooltip]
  );
  return (
    <div className="d-flex align-items-center text-center mb-3 gap-2">
      <h5 className="mb-1">{title}</h5>
      <OverlayTrigger placement="right" overlay={overlay}>
        <img
          src="/info.png"
          alt="More information"
          width={INFO_ICON_SIZE}
          height={INFO_ICON_SIZE}
          loading="lazy"
          decoding="async"
        />
      </OverlayTrigger>
    </div>
  );
};

export const SectionHeader = memo(SectionHeaderComponent);
SectionHeader.displayName = "SectionHeader";

interface YouthMembershipFormProps {
  defaultData?: YouthMembershipFormData;
  showEditModal?: boolean;
  defaultImages?: ImageUrls;
}

function YouthMembershipFormInner({
  showEditModal,
  defaultData,
  defaultImages,
}: YouthMembershipFormProps) {
  const [step, setStep] = useState(0);
  const { user: currentUser } = useAppContext();
  const isAdmin = currentUser?.user?.isAdmin === 1;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    const cityExists = !!currentUser?.user?.city?.trim();
    const genderExists = !!currentUser?.user?.gender?.trim();
    const membershipFormatExists =
      !!currentUser?.user?.mentorshipFormat?.trim();

    if (!cityExists) {
      setStep(0);
    } else if (!genderExists) {
      setStep(1);
    } else if (!membershipFormatExists) {
      setStep(2);
    } else {
      setStep(0);
    }
  }, [
    currentUser?.user?.city,
    currentUser?.user?.gender,
    currentUser?.user?.mentorshipFormat,
  ]);

  useEffect(() => {
    import("./YouthMembershipForms/MentorShipGoalsForm");
    import("./YouthMembershipForms/PersonalProfileForm.tsx");
  }, []);

  const handleNext = useCallback(() => setStep((s) => s + 1), []);
  const handleBack = useCallback(
    () => setStep((s) => Math.max(s - 1, 0)),
    []
  );

  const stepContent = useMemo(() => {
    const userData =
      defaultData ||
      (currentUser?.user as unknown as YouthMembershipFormData);
    const userImages = defaultImages || currentUser?.imageUrls;
    switch (step) {
      case 0:
        return (
          <AgeVerificationForm
            defaultData={userData}
            showEditModal={showEditModal}
            defaultImages={userImages}
            isAdmin={isAdmin}
            handleNext={handleNext}
          />
        );
      case 1:
        return (
          <Suspense fallback={LAZY_FALLBACK}>
            <PersonalProfileForm
              defaultData={userData}
              defaultImages={userImages}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          </Suspense>
        );
      case 2:
        return (
          <Suspense fallback={LAZY_FALLBACK}>
            <MentorshipGoalsForm
              defaultData={userData}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          </Suspense>
        );
      default:
        return null;
    }
  }, [
    step,
    defaultData,
    defaultImages,
    currentUser?.user,
    currentUser?.imageUrls,
    showEditModal,
    isAdmin,
    handleNext,
    handleBack,
  ]);

  return (
    <Row className="rounded-4 p-3 px-4">
      <StepsProgressBar currentStep={step} labels={STEP_LABELS} />
      {stepContent}
    </Row>
  );
}

YouthMembershipFormInner.displayName = "YouthMembershipForm";

const YouthMembershipForm = memo(YouthMembershipFormInner);
YouthMembershipForm.displayName = "YouthMembershipForm";

export default YouthMembershipForm;
